import { generateUID } from "@/canv/utils";
import type { BuildEventStructure, PropertyEventStructure } from "@/eventManager/events/types";
import type {
    CreateTimerData,
    PauseTimerData,
    ResetTimerData,
    RestartTimerData,
    ResumeTimerData,
    TimerEntity,
    TimerEventUpdateType,
    TimerFieldMap,
    TimerUpdateKey,
    UpdateDurationTimerData,
} from "@/timerController/types";
import timerController from "./timerController";

export const createTimerBuildEvent = (data: CreateTimerData): BuildEventStructure[] => {
    return [
        {
            type: "build",
            action: "create",
            entities: [
                {
                    entityType: "timer",
                    UID: generateUID(),
                    ...data,
                },
            ],
        },
    ];
};

type BaseTimerEventData = {
    UID: string;
};

const createTimerPropertyEvent = <T extends object>(
    fixType: TimerEventUpdateType,
    data: BaseTimerEventData & T,
): PropertyEventStructure[] => [
    {
        type: "property",
        UID: data.UID,
        fixType,
        entityType: "timer",
        state: data,
    },
];

export const pauseTimerPropertyEvent = (data: PauseTimerData) =>
    createTimerPropertyEvent("pause", {
        UID: data.UID,
        pausedAt: data.pausedAt,
        updatedAt: data.updatedAt,
        remainingAfterPause: data.remainingAfterPause,
    });

export const resumeTimerPropertyEvent = (data: ResumeTimerData) =>
    createTimerPropertyEvent("resume", {
        UID: data.UID,
        pausedAt: data.pausedAt,
        updatedAt: data.updatedAt,
        remainingAfterPause: data.remainingAfterPause,
        estimatedStopTime: data.estimatedStopTime,
    });

export const updateDurationTimerPropertyEvent = (data: UpdateDurationTimerData) =>
    createTimerPropertyEvent("updateDuration", {
        UID: data.UID,
        duration: data.duration,
        updatedAt: data.updatedAt,
        estimatedStopTime: data.estimatedStopTime,
        remainingAfterPause: data.remainingAfterPause,
    });

export const resetTimerPropertyEvent = (data: ResetTimerData) =>
    createTimerPropertyEvent("reset", {
        UID: data.UID,
        duration: data.duration,
        startedAt: data.startedAt,
        pausedAt: data.pausedAt,
        updatedAt: data.updatedAt,
        estimatedStopTime: data.estimatedStopTime,
        remainingAfterPause: data.remainingAfterPause,
    });

export const restartTimerPropertyEvent = (data: RestartTimerData) =>
    createTimerPropertyEvent("restart", {
        UID: data.UID,
        duration: data.duration,
        startedAt: data.startedAt,
        pausedAt: data.pausedAt,
        updatedAt: data.updatedAt,
        estimatedStopTime: data.estimatedStopTime,
        remainingAfterPause: data.remainingAfterPause,
    });

export const assignTimerUpdate = <K extends TimerUpdateKey>(
    timer: TimerEntity,
    data: Partial<TimerFieldMap>,
    keys: readonly K[],
): void => {
    for (const key of keys) {
        const value = data[key];
        if (value !== undefined) {
            timer[key] = value;
        }
    }
};

export const getDateNow = (): string => {
    const dateNow = new Date().toISOString();

    return new Date(Date.parse(dateNow) - timerController.diffClientServerTimeMS).toISOString();
};
